import { createBrowserRouter, Navigate } from 'react-router-dom';
import React, { Suspense } from 'react';
import { AuthGuard } from '@/components/guards/auth-guard';
import { LoadingSpinner } from '@/components/common/loading-spinner';
import { UserRole } from '@/types/rbac.types';


// Lazy load pages
const LoginPage = React.lazy(() => import('@/features/auth/pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const VehicleRegistryPage = React.lazy(() => import('@/pages/VehicleRegistryPage'));
const FraudCasesPage = React.lazy(() => import('@/pages/FraudCasesPage'));
const ProfilePage = React.lazy(() => import('@/pages/ProfilePage'));
const PaymentsPage = React.lazy(() => import('@/pages/PaymentsPage'));
const UnauthorizedPage = React.lazy(() => import('@/pages/UnauthorizedPage'));
const VinDecodingPage = React.lazy(() => import('@/pages/dvla/VinDecodingPage'));
const StolenVehiclesPage = React.lazy(() => import('@/pages/police/StolenVehiclesPage'));
import { GlobalErrorPage } from '@/pages/ErrorPage';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { StatusScreen } from '@/components/ui/status-screen';
import { Hammer } from 'lucide-react';


// Placeholder components for other routes
const PlaceholderPage = ({ title }: { title: string }) => (
    <DashboardLayout>
        <StatusScreen
            variant="inline"
            icon={Hammer}
            tone="brand"
            code="In development"
            title={`${title} is on its way`}
            description="This module is being built. You'll see it here as soon as it's ready."
        />
    </DashboardLayout>
);


export const router = createBrowserRouter([
    {
        path: '/',
        errorElement: <GlobalErrorPage />,
        children: [
            {
                index: true,
                element: <LoginPage />,
            },
            {
                path: 'forgot-password',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <ForgotPasswordPage />
                    </Suspense>
                ),
            },
            {
                path: 'reset-password',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <ResetPasswordPage />
                    </Suspense>
                ),
            },
            {
                path: 'dashboard',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard>
                            <DashboardPage />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'vin-lookup',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard allowedRoles={[UserRole.DVLA, UserRole.CUSTOMS, UserRole.EOCO, UserRole.POLICE]}>
                            <VinDecodingPage />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'vehicle-history',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard>
                            <VehicleRegistryPage />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'admin/users',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard allowedRoles={[UserRole.DVLA]}>
                            <PlaceholderPage title="User Management" />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'police/stolen',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard allowedRoles={[UserRole.POLICE, UserRole.DVLA]}>
                            <StolenVehiclesPage />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'customs/imports',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard allowedRoles={[UserRole.CUSTOMS, UserRole.DVLA]}>
                            <PlaceholderPage title="Import Verification" />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'eoco/cases',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard allowedRoles={[UserRole.EOCO, UserRole.DVLA]}>
                            <FraudCasesPage />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'reports',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard>
                            <PlaceholderPage title="Reports" />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'profile',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard>
                            <ProfilePage />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'payments',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthGuard>
                            <PaymentsPage />
                        </AuthGuard>
                    </Suspense>
                ),
            },
            {
                path: 'unauthorized',
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <UnauthorizedPage />
                    </Suspense>
                ),
            },
        ]
    },
    {
        path: '*',
        element: <Navigate to="/" replace />,
    },
], {
    future: {
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
    },
});


